"use client";

import { Button, cn, confirm, toast } from "@devhop/ui";
import { PencilIcon, Trash2Icon } from "lucide-react";
import type { WorkshopJobDetail, WorkshopJobPartRow } from "../api/client";
import {
  usePatchWorkshopJobPartMutation,
  useRemoveWorkshopJobPartMutation,
  useWorkshopQuoteQuery,
} from "../api/queries";
import {
  formatWorkshopMoney,
  inventoryStateToneClass,
  WORKSHOP_INVENTORY_STATE_LABELS,
  WORKSHOP_PART_SOURCE_LABELS,
} from "../lib/workshop-labels";
import { workshopToastError } from "../lib/workshop-user-messages";
import { WorkshopQtyStepper } from "./WorkshopQtyStepper";

export function WorkshopJobPartsSummary({
  shopId,
  job,
  canEdit,
  onEdit,
  className,
}: {
  shopId: string;
  job: WorkshopJobDetail;
  canEdit?: boolean;
  onEdit?: () => void;
  className?: string;
}) {
  const patchMut = usePatchWorkshopJobPartMutation(shopId);
  const removeMut = useRemoveWorkshopJobPartMutation(shopId);
  const quoteQ = useWorkshopQuoteQuery(shopId, job.id);
  const parts = job.parts ?? [];
  const billableTotal = quoteQ.data?.partsSubtotal;

  async function removePart(part: WorkshopJobPartRow) {
    const ok = await confirm({
      title: "ລຶບອາໄຫຼ່",
      description: `ລຶບ «${part.name}» (× ${part.qty}) ອອກຈາກໃບງານ ຫຼື ບໍ່?`,
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;
    try {
      await removeMut.mutateAsync({ jobId: job.id, partId: part.id });
      toast.success("ລຶບອາໄຫຼ່ແລ້ວ");
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  async function updatePartQty(part: WorkshopJobPartRow, qty: number) {
    if (qty === part.qty) return;
    if (part.inventoryState === "consumed") {
      toast.error("ຕັດສະຕ໋ອກແລ້ວ — ປ່ຽນຈຳນວນບໍ່ໄດ້");
      return;
    }
    try {
      await patchMut.mutateAsync({
        jobId: job.id,
        partId: part.id,
        body: { qty },
      });
    } catch (err) {
      toast.error(workshopToastError(err));
    }
  }

  if (parts.length === 0) {
    return (
      <div className={cn("space-y-2", className)}>
        <p className="py-1 text-muted-foreground text-sm">ຍັງບໍ່ມີອາໄຫຼ່</p>
        {canEdit && onEdit ? (
          <Button
            type="button"
            variant="outline"
            className="h-10 w-full"
            onClick={onEdit}
          >
            <PencilIcon className="size-4" />
            ເພີ່ມອາໄຫຼ່
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      <ul className="space-y-0">
        {parts.map((p) => {
          const qtyEditable =
            canEdit &&
            p.inventoryState !== "consumed" &&
            p.inventoryState !== "released";

          return (
            <li
              key={p.id}
              className="flex items-start justify-between gap-3 border-border border-b py-2.5 last:border-b-0"
            >
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm leading-snug">{p.name}</p>
                <p className="mt-0.5 text-muted-foreground text-xs">
                  {qtyEditable
                    ? WORKSHOP_PART_SOURCE_LABELS[p.source]
                    : `× ${p.qty} · ${WORKSHOP_PART_SOURCE_LABELS[p.source]}`}
                </p>
                <span
                  className={cn(
                    "mt-1 inline-flex rounded-md border px-1.5 py-0.5 text-[10px] font-medium",
                    inventoryStateToneClass(p.inventoryState),
                  )}
                >
                  {WORKSHOP_INVENTORY_STATE_LABELS[p.inventoryState]}
                </span>
              </div>
              <div className="flex shrink-0 flex-col items-end gap-1.5">
                {qtyEditable ? (
                  <WorkshopQtyStepper
                    size="sm"
                    value={p.qty}
                    disabled={patchMut.isPending}
                    onChange={(qty) => void updatePartQty(p, qty)}
                  />
                ) : (
                  <span className="text-muted-foreground text-xs">× {p.qty}</span>
                )}
                <div className="flex items-center gap-1">
                  <p className="font-semibold text-sm tabular-nums">
                    {p.source === "customer_supplied" && p.lineTotal === 0 ? (
                      <span className="text-violet-800 text-xs">ລູກຄ້ານຳມາ</span>
                    ) : (
                      formatWorkshopMoney(p.lineTotal, p.currency)
                    )}
                  </p>
                  {canEdit ? (
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      className="size-8 text-muted-foreground hover:text-destructive"
                      disabled={removeMut.isPending}
                      onClick={() => void removePart(p)}
                      aria-label="ລຶບອາໄຫຼ່"
                    >
                      <Trash2Icon className="size-4" />
                    </Button>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="flex items-center justify-between gap-2 text-sm">
        <span className="text-muted-foreground">ລວມຄ່າອາໄຫຼ່ (ຮ້ານ)</span>
        <span className="font-bold tabular-nums">
          {quoteQ.isLoading ? (
            <span className="text-muted-foreground">…</span>
          ) : billableTotal != null ? (
            formatWorkshopMoney(billableTotal, job.currency)
          ) : (
            <span className="text-amber-800 text-xs">ຕ້ອງຕັ້ງອັດຕາແລກປ່ຽນ</span>
          )}
        </span>
      </div>
      {canEdit && onEdit ? (
        <Button
          type="button"
          variant="outline"
          className="h-10 w-full"
          onClick={onEdit}
        >
          <PencilIcon className="size-4" />
          ແກ້ໄຂອາໄຫຼ່
        </Button>
      ) : null}
    </div>
  );
}
